import { supabase } from "../supabase";
import {
  formatSupabaseError,
  loadEstimateTree,
  loadMaterialsCatalog,
  LABOUR_PRICING_METHOD,
} from "./estimates";
import {
  computeLabourLineTotal,
  computeLabourSqft,
} from "./estimateTotals";
import { getLabourClientRate, getMaterialClientUnitPrice, hasPriceOverride } from "./estimateLinePricing";
import { computeLivePricing } from "./estimateTax";
import { requireEstimateConvert } from "./financialGuards";

export const QUOTE_LINE_TYPE = Object.freeze({
  MATERIAL: "material",
  LABOUR: "labour",
});

export const QUOTE_LINE_ORIGIN = Object.freeze({
  ESTIMATE_CONVERSION: "estimate_conversion",
});

const CONVERSION_LINES_KEY = "conversion_lines";
const CONVERSION_LINE_COUNT_KEY = "conversion_line_count";

export function quoteLineIdentity(line) {
  if (line?.estimate_room_material_id) {
    return `material:${line.estimate_room_material_id}`;
  }
  if (line?.estimate_room_labour_id) {
    return `labour:${line.estimate_room_labour_id}`;
  }
  return `sort:${line?.sort_order}:${line?.line_type}:${line?.description_snapshot}`;
}

/**
 * The summary row is the commercial version for this conversion attempt.
 * conversion_lines is that attempt's line payloads, stored beside waste/travel/misc
 * so a retry can finish the line table without reading the estimate again.
 */
export function stampQuoteSummary(summary, lines = []) {
  const adjustments = summary?.quoted_adjustments_snapshot || {};
  return {
    ...summary,
    quoted_adjustments_snapshot: {
      waste: roundMoney(Number(adjustments.waste) || 0),
      travel: roundMoney(Number(adjustments.travel) || 0),
      misc: roundMoney(Number(adjustments.misc) || 0),
      [CONVERSION_LINE_COUNT_KEY]: lines.length,
      [CONVERSION_LINES_KEY]: lines,
    },
  };
}

export function quoteSnapshotRepairPlan(summary, existingLines = []) {
  const adjustments = summary?.quoted_adjustments_snapshot || {};
  const canonical = adjustments[CONVERSION_LINES_KEY];
  const present = new Set(
    (existingLines || []).map((line) => quoteLineIdentity(line)).filter(Boolean),
  );

  if (Array.isArray(canonical)) {
    const missingLines = canonical.filter(
      (line) => !present.has(quoteLineIdentity(line)),
    );
    return {
      complete: missingLines.length === 0,
      missingLines,
      irreparable: false,
    };
  }

  const materialTotal = roundMoney(
    (existingLines || [])
      .filter((line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL)
      .reduce((sum, line) => sum + Number(line.quoted_line_total_snapshot || 0), 0),
  );
  const labourTotal = roundMoney(
    (existingLines || [])
      .filter((line) => line.line_type === QUOTE_LINE_TYPE.LABOUR)
      .reduce((sum, line) => sum + Number(line.quoted_line_total_snapshot || 0), 0),
  );
  const totalsMatch =
    materialTotal === roundMoney(Number(summary?.quoted_materials_total_snapshot) || 0) &&
    labourTotal === roundMoney(Number(summary?.quoted_labour_total_snapshot) || 0);
  const expectedCount = adjustments[CONVERSION_LINE_COUNT_KEY];
  const countMatches =
    !Number.isFinite(Number(expectedCount)) ||
    (existingLines || []).length === Number(expectedCount);

  if (totalsMatch && countMatches) {
    return { complete: true, missingLines: [], irreparable: false };
  }

  return { complete: false, missingLines: [], irreparable: true };
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

/**
 * Customer unit price for an estimate material line — shared with
 * estimateTotals via estimateLinePricing (F01): frozen sell price → live
 * catalog sell price → cost fallback, then the line's "Your price" override.
 */
export function getMaterialSellUnitPrice(row, materialsCatalog = []) {
  return getMaterialClientUnitPrice(row, materialsCatalog);
}

export function computeMaterialQuoteLineTotal(row, materialsCatalog = []) {
  const quantity =
    row.quantity != null && row.quantity !== "" ? Number(row.quantity) : null;
  const unitPrice = getMaterialSellUnitPrice(row, materialsCatalog);

  if (quantity == null || !Number.isFinite(quantity)) {
    return 0;
  }

  return roundMoney(quantity * unitPrice);
}

function buildMaterialLineSnapshot(
  material,
  { projectId, estimateId, sortOrder },
  materialsCatalog,
) {
  const unitPrice = getMaterialSellUnitPrice(material, materialsCatalog);
  const quantity =
    material.quantity != null && material.quantity !== ""
      ? Number(material.quantity)
      : null;
  const lineTotal = computeMaterialQuoteLineTotal(material, materialsCatalog);

  return {
    project_id: projectId,
    estimate_id_snapshot: estimateId,
    line_type: QUOTE_LINE_TYPE.MATERIAL,
    line_origin: QUOTE_LINE_ORIGIN.ESTIMATE_CONVERSION,
    description_snapshot: material.material_name_snapshot,
    quoted_quantity_snapshot: quantity,
    unit_label_snapshot: material.unit_of_measure_snapshot || null,
    quoted_unit_price_snapshot: roundMoney(unitPrice),
    quoted_line_total_snapshot: lineTotal,
    sort_order: sortOrder,
    estimate_room_material_id: material.room_material_id,
    estimate_room_labour_id: null,
    material_id: material.material_id || null,
    estimate_feature_id: material.estimate_feature_id || null,
    source_line_metadata: {
      sku_snapshot: material.sku_snapshot || null,
      unit_of_measure_snapshot: material.unit_of_measure_snapshot || null,
      unit_cost_snapshot: Number(material.unit_cost_snapshot) || 0,
      notes: material.notes || null,
      price_overridden: hasPriceOverride(material),
    },
  };
}

function buildLabourLineSnapshot(labour, workType, { projectId, estimateId, sortOrder }) {
  const method = labour.pricing_method || LABOUR_PRICING_METHOD.HOURLY;
  const unitRate = getLabourClientRate(labour);
  const lineTotal = roundMoney(computeLabourLineTotal(labour, workType));

  let quotedQuantity = null;
  let unitLabel = null;

  if (method === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    quotedQuantity = roundMoney(computeLabourSqft(labour, workType));
    unitLabel = "sqft";
  } else {
    quotedQuantity =
      labour.estimated_hours != null ? Number(labour.estimated_hours) : null;
    unitLabel = "hrs";
  }

  return {
    project_id: projectId,
    estimate_id_snapshot: estimateId,
    line_type: QUOTE_LINE_TYPE.LABOUR,
    line_origin: QUOTE_LINE_ORIGIN.ESTIMATE_CONVERSION,
    description_snapshot: labour.labour_activity,
    quoted_quantity_snapshot: quotedQuantity,
    unit_label_snapshot: unitLabel,
    quoted_unit_price_snapshot: roundMoney(unitRate),
    quoted_line_total_snapshot: lineTotal,
    sort_order: sortOrder,
    estimate_room_material_id: null,
    estimate_room_labour_id: labour.labour_id,
    material_id: null,
    estimate_feature_id: labour.estimate_feature_id || null,
    source_line_metadata: {
      price_overridden: hasPriceOverride(labour),
      surface_section_ids: labour.surface_section_ids || null,
      pricing_method: method,
      estimated_hours:
        labour.estimated_hours != null ? Number(labour.estimated_hours) : null,
      hourly_rate_snapshot: roundMoney(unitRate),
      notes: labour.notes || null,
    },
  };
}

/**
 * Build summary + unified quote line snapshots from a loaded estimate tree.
 * Uses sell pricing for materials and estimate labour pricing rules for labour lines.
 */
export function buildQuoteLineSnapshotsFromEstimateTree(
  estimateTree,
  { projectId, materialsCatalog = [] },
) {
  if (!estimateTree?.estimate_id) {
    throw new Error("Estimate tree is missing estimate_id.");
  }
  if (!projectId) {
    throw new Error("projectId is required to build quote snapshots.");
  }

  const estimateId = estimateTree.estimate_id;
  const lines = [];
  let sortOrder = 0;

  for (const location of estimateTree.locations || []) {
    for (const workType of location.workTypes || []) {
      for (const material of workType.materials || []) {
        lines.push(
          buildMaterialLineSnapshot(
            material,
            { projectId, estimateId, sortOrder },
            materialsCatalog,
          ),
        );
        sortOrder += 1;
      }

      for (const labour of workType.labour || []) {
        lines.push(
          buildLabourLineSnapshot(labour, workType, {
            projectId,
            estimateId,
            sortOrder,
          }),
        );
        sortOrder += 1;
      }
    }
  }

  const quotedMaterialsTotal = roundMoney(
    lines
      .filter((line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL)
      .reduce((sum, line) => sum + line.quoted_line_total_snapshot, 0),
  );

  const quotedLabourTotal = roundMoney(
    lines
      .filter((line) => line.line_type === QUOTE_LINE_TYPE.LABOUR)
      .reduce((sum, line) => sum + line.quoted_line_total_snapshot, 0),
  );

  const wasteAdjustment = roundMoney(Number(estimateTree.waste_adjustment) || 0);
  const travelAdjustment = roundMoney(Number(estimateTree.travel_adjustment) || 0);
  const miscAdjustment = roundMoney(Number(estimateTree.misc_adjustment) || 0);

  const quotedAdjustments = {
    waste: wasteAdjustment,
    travel: travelAdjustment,
    misc: miscAdjustment,
  };

  const quotedGrandTotal = computeLivePricing({
    estimateSubtotal: quotedMaterialsTotal + quotedLabourTotal,
    waste: wasteAdjustment,
    travel: travelAdjustment,
    misc: miscAdjustment,
    wasteTaxable: estimateTree.waste_taxable !== false,
    travelTaxable: estimateTree.travel_taxable !== false,
    miscTaxable: estimateTree.misc_taxable !== false,
    adjustmentMode: estimateTree.subtotal_adjustment_mode,
    adjustmentValue: estimateTree.subtotal_adjustment_value,
    finalPrice: estimateTree.final_estimate_price,
  }).preTax;

  const summary = {
    project_id: projectId,
    estimate_id_snapshot: estimateId,
    quoted_materials_total_snapshot: quotedMaterialsTotal,
    quoted_labour_total_snapshot: quotedLabourTotal,
    quoted_adjustments_snapshot: quotedAdjustments,
    quoted_grand_total_snapshot: quotedGrandTotal,
  };

  const materialLineCount = lines.filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
  ).length;
  const labourLineCount = lines.filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.LABOUR,
  ).length;

  return {
    summary,
    lines,
    materialLineCount,
    labourLineCount,
    quotedGrandTotal,
  };
}

export async function loadProjectQuoteSummary(projectId) {
  if (!projectId) return null;

  const { data, error } = await supabase
    .from("project_quote_summary_snapshots")
    .select("*")
    .eq("project_id", projectId)
    .maybeSingle();

  if (error) wrapSupabaseError(error);
  return data;
}

export async function loadProjectQuoteLines(projectId, { lineType } = {}) {
  if (!projectId) return [];

  let query = supabase
    .from("project_quote_line_snapshots")
    .select("*")
    .eq("project_id", projectId)
    .order("sort_order");

  if (lineType) {
    query = query.eq("line_type", lineType);
  }

  const { data, error } = await query;
  if (error) wrapSupabaseError(error);
  return data || [];
}

async function resumeStoredQuoteSnapshot(projectId, existing) {
  const storedLines = await loadProjectQuoteLines(projectId);
  const plan = quoteSnapshotRepairPlan(existing, storedLines);

  if (plan.irreparable) {
    throw new Error(
      "Quote snapshot is incomplete and the original lines were not stored. Refusing to mix a newer estimate.",
    );
  }

  if (plan.missingLines.length) {
    const { error } = await supabase
      .from("project_quote_line_snapshots")
      .insert(plan.missingLines);
    if (error) wrapSupabaseError(error);
  }

  return {
    skipped: plan.missingLines.length === 0,
    repaired: plan.missingLines.length > 0,
    summarySnapshotId: existing.summary_snapshot_id,
    materialLineCount: plan.missingLines.filter(
      (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
    ).length,
    labourLineCount: plan.missingLines.filter(
      (line) => line.line_type === QUOTE_LINE_TYPE.LABOUR,
    ).length,
    quotedGrandTotal: Number(existing.quoted_grand_total_snapshot) || 0,
  };
}

/**
 * Insert quote summary + line snapshots.
 * A summary that already exists is finished from the lines stored on that
 * summary. The live estimate is not read again.
 */
export async function insertProjectQuoteSnapshots({
  projectId,
  summary,
  lines,
  userId,
}) {
  if (!projectId) {
    throw new Error("projectId is required.");
  }
  if (!summary?.estimate_id_snapshot) {
    throw new Error("summary.estimate_id_snapshot is required.");
  }

  const existing = await loadProjectQuoteSummary(projectId);
  if (existing) {
    return resumeStoredQuoteSnapshot(projectId, existing);
  }

  const summaryPayload = {
    ...stampQuoteSummary(summary, lines || []),
    snapshotted_by: userId ?? null,
  };

  const { data: insertedSummary, error: summaryError } = await supabase
    .from("project_quote_summary_snapshots")
    .insert(summaryPayload)
    .select("summary_snapshot_id, quoted_grand_total_snapshot")
    .single();

  if (summaryError) {
    if (summaryError.code === "23505") {
      const raced = await loadProjectQuoteSummary(projectId);
      if (
        raced?.estimate_id_snapshot &&
        raced.estimate_id_snapshot !== summary.estimate_id_snapshot
      ) {
        throw new Error("Project quote snapshot belongs to another estimate.");
      }
      if (raced) {
        return resumeStoredQuoteSnapshot(projectId, raced);
      }
    }
    wrapSupabaseError(summaryError);
  }

  if (lines?.length) {
    const { error: linesError } = await supabase
      .from("project_quote_line_snapshots")
      .insert(lines);

    if (linesError) wrapSupabaseError(linesError);
  }

  const materialLineCount = (lines || []).filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.MATERIAL,
  ).length;
  const labourLineCount = (lines || []).filter(
    (line) => line.line_type === QUOTE_LINE_TYPE.LABOUR,
  ).length;

  return {
    skipped: false,
    repaired: false,
    summarySnapshotId: insertedSummary.summary_snapshot_id,
    materialLineCount,
    labourLineCount,
    quotedGrandTotal: Number(insertedSummary.quoted_grand_total_snapshot) || 0,
  };
}

/**
 * End-to-end: load estimate tree, build snapshots, insert if not already present.
 */
export async function ensureProjectQuoteSnapshotsFromEstimate({
  actor,
  projectId,
  estimateId,
  userId,
  estimateTree = null,
  materialsCatalog = null,
}) {
  requireEstimateConvert(actor);

  if (!projectId || !estimateId) {
    throw new Error("projectId and estimateId are required.");
  }

  const existing = await loadProjectQuoteSummary(projectId);
  if (existing) {
    if (
      existing.estimate_id_snapshot &&
      existing.estimate_id_snapshot !== estimateId
    ) {
      throw new Error("Project quote snapshot belongs to another estimate.");
    }
    return resumeStoredQuoteSnapshot(projectId, existing);
  }

  const tree = estimateTree || (await loadEstimateTree(actor, estimateId));
  if (
    tree?.organization_id &&
    actor?.organization_id &&
    tree.organization_id !== actor.organization_id
  ) {
    throw new Error("Estimate is outside this organization.");
  }
  const catalog = materialsCatalog ?? (await loadMaterialsCatalog());

  const { summary, lines, materialLineCount, labourLineCount, quotedGrandTotal } =
    buildQuoteLineSnapshotsFromEstimateTree(tree, {
      projectId,
      materialsCatalog: catalog,
    });

  const result = await insertProjectQuoteSnapshots({
    projectId,
    summary,
    lines,
    userId,
  });

  return {
    ...result,
    materialLineCount: result.skipped ? 0 : materialLineCount,
    labourLineCount: result.skipped ? 0 : labourLineCount,
    quotedGrandTotal: result.skipped ? result.quotedGrandTotal : quotedGrandTotal,
  };
}
